import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Peak Hours Analysis Chart
 * Area chart showing hourly engagement percentage with pagination
 */
const MBFPeakHoursChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = 2;

    // Default data
    const defaultData = [
        { time: '08:00', engagement_percentage: 20 },
        { time: '09:00', engagement_percentage: 35 },
        { time: '10:00', engagement_percentage: 55 },
        { time: '11:00', engagement_percentage: 70 },
        { time: '12:00', engagement_percentage: 65 },
        { time: '13:00', engagement_percentage: 85 },
        { time: '14:00', engagement_percentage: 95 },
        { time: '15:00', engagement_percentage: 78 },
    ];

    // Transform API data to chart format
    // API returns: [{ time: '08:00', engagement_percentage: 0.0, reservation_count: 0 }, ...]
    const chartData = data.length > 0 ? data : defaultData;
    const hours = chartData.map(item => item.time || '');
    const engagementValues = chartData.map(item => item.engagement_percentage || 0);

    const handlePrevPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;

            // Create gradient fill
            const gradient = ctx.createLinearGradient(0, 0, 0, 250);
            gradient.addColorStop(0, 'rgba(121, 135, 255, 0.4)');
            gradient.addColorStop(1, 'rgba(121, 135, 255, 0.05)');

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: hours,
                    datasets: [
                        {
                            label: 'Engagement',
                            data: engagementValues,
                            borderColor: '#7987FF',
                            backgroundColor: gradient,
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 0,
                            pointHoverRadius: 6,
                            pointHoverBackgroundColor: '#7987FF',
                            pointHoverBorderColor: '#ffffff',
                            pointHoverBorderWidth: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => `Engagement: ${context.raw}%`,
                            },
                        },
                        datalabels: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: true, color: '#f0f0f0' },
                            border: { display: false },
                            ticks: {
                                color: '#222222',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            max: 100,
                            ticks: {
                                stepSize: 20,
                                color: '#444444',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, hours, engagementValues]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Peak Hours Analysis
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        minHeight: 0,
                    }}
                >
                    {/* Y-Axis Label */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            Engagement (In %)
                        </Typography>
                    </Box>

                    {/* Chart + Controls */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            minHeight: 0,
                        }}
                    >
                        {/* Chart */}
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <canvas ref={chartRef} />
                        </Box>

                        {/* X-Axis Label */}
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                fontWeight: 500,
                                textAlign: 'center',
                            }}
                        >
                            Time
                        </Typography>

                        {/* Pagination */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '8px',
                                flexShrink: 0,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                sx={{
                                    bgcolor: '#fafafa',
                                    border: '1px solid #e6e6e6',
                                    borderRadius: '8px',
                                    padding: '4px',
                                    '&:hover': { bgcolor: '#f0f0f0' },
                                    '&:disabled': { opacity: 0.5 },
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: '16px', color: '#4d4d4d' }} />
                            </IconButton>

                            <Box
                                sx={{
                                    bgcolor: '#fafafa',
                                    border: '1px solid #e6e6e6',
                                    borderRadius: '8px',
                                    padding: '4px 8px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#4d4d4d',
                                        fontSize: '10px',
                                        fontFamily: '"Roboto", sans-serif',
                                    }}
                                >
                                    {String(currentPage).padStart(2, '0')}/{String(totalPages).padStart(2, '0')}
                                </Typography>
                            </Box>

                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                sx={{
                                    bgcolor: '#fafafa',
                                    border: '1px solid #e6e6e6',
                                    borderRadius: '8px',
                                    padding: '4px',
                                    '&:hover': { bgcolor: '#f0f0f0' },
                                    '&:disabled': { opacity: 0.5 },
                                }}
                            >
                                <ChevronRightIcon sx={{ fontSize: '16px', color: '#4d4d4d' }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFPeakHoursChart;
